import * as yup from "yup";

export const CHECK_IN_METRIC_CODES = ["mood", "sleep", "appetite"] as const;

export type CheckInMetricCode = typeof CHECK_IN_METRIC_CODES[number];

export interface CheckInRegistration {
  metricId: number;
  metricValueId: number | null;
  emotionIds: number[];
}

export interface CheckInEntry {
  id: number;
  checkedInAt: string;
  isDayStart: boolean;
  processId: number | null;
  registrations: CheckInRegistration[];
}

export interface PatientCheckInDay {
  date: string;
  feelsGood: boolean | null;
  signals: CheckInMetricCode[];
  entries: CheckInEntry[];
}

export interface CheckInMetricValue {
  id: number;
  value: number;
  name: string;
}

export interface CheckInMetric {
  id: number;
  code: CheckInMetricCode;
  name: string;
  metricValues: CheckInMetricValue[];
}

export interface CheckInEmotion {
  id: number;
  metricValueId: number;
  name: string;
}

export interface CheckInProcess {
  id: number;
  code: string;
  name: string;
  metrics: string[];
}

export interface CheckInDictionary {
  processes: CheckInProcess[];
  metrics: CheckInMetric[];
  emotions: CheckInEmotion[];
}

const metricCodeSchema = yup
  .mixed<CheckInMetricCode>()
  .oneOf([...CHECK_IN_METRIC_CODES])
  .required();

const checkInRegistrationSchema = yup.object({
  metricId: yup.number().required(),
  metricValueId: yup.number().nullable().defined(),
  emotionIds: yup.array().of(yup.number().required()).required(),
});

const checkInEntrySchema = yup.object({
  id: yup.number().required(),
  checkedInAt: yup.string().required(),
  isDayStart: yup.boolean().required(),
  processId: yup.number().nullable().defined(),
  registrations: yup.array().of(checkInRegistrationSchema).required(),
});

export const patientCheckInDaySchema = yup.object({
  date: yup.string().required(),
  feelsGood: yup.boolean().nullable().defined(),
  signals: yup.array().of(metricCodeSchema).required(),
  entries: yup.array().of(checkInEntrySchema).required(),
});

const checkInMetricValueSchema = yup.object({
  id: yup.number().required(),
  value: yup.number().required(),
  name: yup.string().required(),
});

const checkInMetricSchema = yup.object({
  id: yup.number().required(),
  code: metricCodeSchema,
  name: yup.string().required(),
  metricValues: yup.array().of(checkInMetricValueSchema).required(),
});

const checkInEmotionSchema = yup.object({
  id: yup.number().required(),
  metricValueId: yup.number().required(),
  name: yup.string().required(),
});

const checkInProcessSchema = yup.object({
  id: yup.number().required(),
  code: yup.string().required(),
  name: yup.string().required(),
  metrics: yup.array().of(yup.string().required()).required(),
});

export const checkInDictionarySchema = yup.object({
  processes: yup.array().of(checkInProcessSchema).required(),
  metrics: yup.array().of(checkInMetricSchema).required(),
  emotions: yup.array().of(checkInEmotionSchema).required(),
});
